import React from "react";
import { Plus, CheckCircle2, Check } from "lucide-react";
import "./CampaignTab.css";

const campaignOverview = [
  { label: "Campaign Name", value: "Summer Campaign 2026" },
  { label: "Campaign Type", value: "Seasonal / Public" },
  { label: "Duration", value: "Oct 14 – Oct 25, 2026" },
];

const eligibilityStatus = "Active";

const associatedCoupons = [
  {
    id: 1,
    code: "SUMMER20",
    type: "Percentage 20%",
    redemptions: "1,248",
    discount: "249,600 EGP",
    status: "Active",
  },
  {
    id: 2,
    code: "FREESHIP50",
    type: "Free Shipping",
    redemptions: "420",
    discount: "15,000 EGP",
    status: "Active",
  },
  {
    id: 3,
    code: "BLACKFRIDAY20",
    type: "Percentage 20%",
    redemptions: "1,248",
    discount: "249,600 EGP",
    status: "Active",
  },
  {
    id: 4,
    code: "FREESHIP50",
    type: "Free Shipping",
    redemptions: "420",
    discount: "15,000 EGP",
    status: "Active",
  },
];

const performanceMetrics = [
  { label: "Total Campaign Budget", value: "1,000,000 EGP" },
  { label: "Total Revenue Generated", value: "1,998,400 EGP" },
  { label: "Campaign ROI", value: "2.4x" },
];

const campaignSettings = [
  { label: "Campaign ID", value: "CMP-8821" },
  { label: "Owner / Dept", value: "Marketing team" },
  { label: "Funding Split", value: "Platform (70%) / Provider (30%)" },
  { label: "Auto-Stop on Budget", value: "Enabled" },
  { label: "Target Region", value: "All Regions" },
  { label: "Priority", value: "High" },
];

const launchChecklist = [
  "Campaign duration is valid",
  "Total budget allocated",
  "Cross-coupon stacking checked",
  "Merchant approval received",
];

function StepBadge({ n }) {
  return <span className="ct-step-badge">{n}</span>;
}

export default function CampaignTab() {
  return (
    <div className="ct-grid">
      {/* ---------- MAIN COLUMN ---------- */}
      <div className="ct-main">
        <div className="ct-panel">
          {/* 1. Campaign Overview */}
          <div className="ct-panel-section">
            <div className="ct-card-header">
              <StepBadge n={1} />
              <h3 className="ct-card-title">Campaign Overview</h3>
            </div>
            <p className="ct-desc">
              Details and metadata of the campaign linked to this coupon.
            </p>

            <div className="ct-overview-grid">
              {campaignOverview.map((f) => (
                <div className="ct-overview-item" key={f.label}>
                  <span className="ct-overview-label">{f.label}</span>
                  <span className="ct-overview-value">{f.value}</span>
                </div>
              ))}
              <div className="ct-overview-item">
                <span className="ct-overview-label">Eligibility Status</span>
                <span className="ct-eligibility-pill">{eligibilityStatus}</span>
              </div>
            </div>
          </div>

          <div className="ct-panel-divider" />

          {/* 2. Associated Coupons */}
          <div className="ct-panel-section">
            <div className="ct-card-header-row">
              <div className="ct-card-header">
                <StepBadge n={2} />
                <h3 className="ct-card-title">Associated Coupons</h3>
              </div>
              <button className="ct-add-btn">
                <Plus size={14} /> Link Coupon
              </button>
            </div>
            <p className="ct-desc">
              Other coupons running under the same campaign.
            </p>

            <div className="ct-table-wrap">
              <table className="ct-table">
                <thead>
                  <tr>
                    <th className="ct-col-num">#</th>
                    <th>Coupon Code</th>
                    <th>Discount Type</th>
                    <th>Redemptions</th>
                    <th>Total Discount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {associatedCoupons.map((c) => (
                    <tr key={c.id}>
                      <td className="ct-col-num">{c.id}</td>
                      <td>{c.code}</td>
                      <td>{c.type}</td>
                      <td>{c.redemptions}</td>
                      <td className="ct-cell-bold">{c.discount}</td>
                      <td>
                        <span className="ct-status-pill">{c.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="ct-panel-divider" />

          {/* 3. Campaign Performance Metrics */}
          <div className="ct-panel-section">
            <div className="ct-card-header">
              <StepBadge n={3} />
              <h3 className="ct-card-title">Campaign Performance Metrics</h3>
            </div>
            <p className="ct-desc">
              Aggregated financial distribution for the entire campaign.
            </p>

            <div className="ct-metrics-grid">
              {performanceMetrics.map((m) => (
                <div className="ct-overview-item" key={m.label}>
                  <span className="ct-overview-label">{m.label}</span>
                  <span className="ct-overview-value">{m.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ---------- SIDEBAR ---------- */}
      <div className="ct-sidebar">
        {/* Campaign Health */}
        <div className="ct-card">
          <h3 className="ct-card-title">Campaign Health</h3>
          <div className="ct-health-row">
            <span className="ct-health-icon">
              <CheckCircle2 size={18} color="#fff" />
            </span>
            <span className="ct-health-title">Healthy</span>
          </div>
          <p className="ct-health-line">
            Campaign is active and operating within budget limits.
          </p>
        </div>

        {/* Campaign Settings */}
        <div className="ct-card">
          <h3 className="ct-card-title">Campaign Settings</h3>
          <div className="ct-settings-list">
            {campaignSettings.map((s, i) => (
              <div className="ct-setting-item" key={s.label}>
                <span className="ct-setting-num">{i + 1}</span>
                <span className="ct-setting-label">{s.label}</span>
                <span className="ct-setting-value">{s.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Launch Checklist */}
        <div className="ct-card">
          <h3 className="ct-card-title">Launch Checklist</h3>
          <div className="ct-checklist">
            {launchChecklist.map((item) => (
              <div className="ct-check-item" key={item}>
                <span className="ct-check-icon">
                  <Check size={12} color="#fff" />
                </span>
                <span className="ct-check-label">{item}</span>
                <span className="ct-pass-pill">Pass</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
